import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type LockPayload = {
  ownerTabId: string;
  ts: number;
  expiresAt: number;
};

type BroadcastMessage =
  | { type: 'acquire'; key: string; payload: LockPayload }
  | { type: 'release'; key: string }
  | { type: 'heartbeat'; key: string; payload: LockPayload };

/**
 * Options for configuring the cross-tab lock behavior.
 * - ttlMs: Lock time-to-live in milliseconds before considered stale.
 * - heartbeatMs: Interval to extend the lock while owned.
 * - channelName: BroadcastChannel name for cross-tab notifications.
 */
export interface CrossTabLockOptions {
  ttlMs?: number;
  heartbeatMs?: number;
  channelName?: string;
}

/**
 * Public API returned by useCrossTabLock hook.
 * - key: Storage key used by the lock.
 * - locked: Whether a valid lock exists.
 * - isOwner: Whether this tab owns the current lock.
 * - acquire: Attempt to acquire the lock; returns true if successful.
 * - release: Release the lock when owned.
 */
export interface CrossTabLock {
  key: string;
  locked: boolean;
  isOwner: boolean;
  acquire: () => boolean;
  release: () => void;
}

const DEFAULT_TTL = 5000;
const DEFAULT_HEARTBEAT = 1500;
const DEFAULT_CHANNEL = 'diamond-locks';

function safeNow() {
  return Date.now();
}

function readLock(key: string): LockPayload | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LockPayload;
    return parsed ?? null;
  } catch {
    return null;
  }
}

function writeLock(key: string, payload: LockPayload) {
  localStorage.setItem(key, JSON.stringify(payload));
}

function clearLock(key: string) {
  localStorage.removeItem(key);
}

/**
 * Cross-tab lock using BroadcastChannel with localStorage storage-event fallback.
 * Keys are namespaced as `diamond:lock:{scope}:{entityId}`. The owner sends
 * a heartbeat to extend TTL while work is in-flight and releases on settle.
 */
export function useCrossTabLock(
  scope: string,
  entityId: string,
  options: CrossTabLockOptions = {}
): CrossTabLock {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL;
  const heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT;
  const channelName = options.channelName ?? DEFAULT_CHANNEL;

  const key = useMemo(
    () => `diamond:lock:${scope}:${entityId}`,
    [scope, entityId]
  );

  const tabIdRef = useRef<string>(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Date.now()}`
  );

  const [locked, setLocked] = useState<boolean>(false);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const heartbeatRef = useRef<number | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const current = readLock(key);
    const now = safeNow();
    if (current && current.expiresAt > now) {
      setLocked(true);
      setIsOwner(current.ownerTabId === tabIdRef.current);
    } else if (current && current.expiresAt <= now) {
      clearLock(key);
      setLocked(false);
      setIsOwner(false);
    }
  }, [key]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const cleanups: Array<() => void> = [];

    if ('BroadcastChannel' in window) {
      const bc = new BroadcastChannel(channelName);
      channelRef.current = bc;
      const onMessage = (ev: MessageEvent<BroadcastMessage>) => {
        const msg = ev.data;
        if (!msg) return;
        if (msg.type !== 'acquire' && msg.type !== 'release' && msg.type !== 'heartbeat')
          return;
        if (msg.key !== key) return;
        if (msg.type === 'release') {
          setLocked(false);
          setIsOwner(false);
          return;
        }
        const { payload } = msg;
        const now = safeNow();
        if (payload.expiresAt > now) {
          setLocked(true);
          setIsOwner(payload.ownerTabId === tabIdRef.current);
        }
      };
      bc.addEventListener('message', onMessage);
      cleanups.push(() => {
        bc.removeEventListener('message', onMessage);
        try {
          bc.close();
        } catch {
          void 0;
        }
        channelRef.current = null;
      });
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      const now = safeNow();
      if (e.newValue == null) {
        setLocked(false);
        setIsOwner(false);
        return;
      }
      try {
        const payload = JSON.parse(e.newValue) as LockPayload;
        if (payload.expiresAt > now) {
          setLocked(true);
          setIsOwner(payload.ownerTabId === tabIdRef.current);
        }
      } catch {
        void 0;
      }
    };
    window.addEventListener('storage', onStorage);
    cleanups.push(() => window.removeEventListener('storage', onStorage));

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [channelName, key]);

  const broadcast = useCallback((msg: BroadcastMessage) => {
    const ch = channelRef.current;
    if (ch) {
      try {
        ch.postMessage(msg);
      } catch {
        // ignore if channel already closed
      }
    }
    if (msg.type === 'acquire' || msg.type === 'heartbeat') {
      try {
        writeLock(msg.key, msg.payload);
      } catch {
        void 0;
      }
    }
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const acquire = useCallback((): boolean => {
    const now = safeNow();
    const existing = readLock(key);
    if (
      existing &&
      existing.expiresAt > now &&
      existing.ownerTabId !== tabIdRef.current
    ) {
      setLocked(true);
      setIsOwner(false);
      return false;
    }
    const payload: LockPayload = {
      ownerTabId: tabIdRef.current,
      ts: now,
      expiresAt: now + ttlMs,
    };
    try {
      writeLock(key, payload);
    } catch {
      void 0;
    }
    setLocked(true);
    setIsOwner(true);
    broadcast({ type: 'acquire', key, payload });
    stopHeartbeat();
    heartbeatRef.current = window.setInterval(() => {
      const p: LockPayload = {
        ownerTabId: tabIdRef.current,
        ts: safeNow(),
        expiresAt: safeNow() + ttlMs,
      };
      try {
        writeLock(key, p);
      } catch {
        void 0;
      }
      broadcast({ type: 'heartbeat', key, payload: p });
    }, heartbeatMs);

    return true;
  }, [broadcast, heartbeatMs, key, stopHeartbeat, ttlMs]);

  const release = useCallback(() => {
    const existing = readLock(key);
    if (existing && existing.ownerTabId !== tabIdRef.current) {
      return;
    }
    stopHeartbeat();
    clearLock(key);
    setLocked(false);
    setIsOwner(false);
    broadcast({ type: 'release', key });
  }, [broadcast, key, stopHeartbeat]);

  useEffect(() => () => stopHeartbeat(), [stopHeartbeat]);

  return { key, locked, isOwner, acquire, release };
}
